import { create } from "zustand";
import { User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

interface UserProfile {
	id: string;
	username: string;
	full_name?: string | null;
	address?: string | null;
	phone?: string | null;
}

interface AdminVerification {
	isAdmin: boolean;
	mfaRequired?: boolean;
	role?: string | null;
	permissions?: string[];
}

interface AuthState {
	user: User | null;
	profile: UserProfile | null;
	loading: boolean;
	isAdmin: boolean;
	adminVerificationCache: AdminVerification | null;
	adminVerificationCacheTime: number;
	/** Token di sessione per cui vale la cache: cambia dopo la 2FA, al refresh e al cambio utente. */
	adminVerificationCacheToken: string | null;
	setUser: (user: User | null) => void;
	setProfile: (profile: UserProfile | null) => void;
	checkUser: () => Promise<void>;
	signIn: (email: string, password: string) => Promise<{ error: any | null }>;
	signUp: (
		email: string,
		password: string,
		username: string,
	) => Promise<{ error: any | null; data: any | null }>;
	signOut: () => Promise<void>;
	fetchUserProfile: (userId: string) => Promise<void>;
	verifyAdminRole: (forceRefresh?: boolean) => Promise<AdminVerification>;
}

// Cache TTL: 15 minuti (900000 ms)
const ADMIN_CACHE_TTL = 15 * 60 * 1000;

/**
 * Validazione password secondo i criteri di sicurezza:
 * - Almeno 8 caratteri
 * - Almeno una lettera maiuscola
 * - Almeno un numero
 * - Almeno un carattere speciale
 */
function validatePassword(password: string): {
	isValid: boolean;
	errors: string[];
} {
	const errors: string[] = [];

	if (password.length < 8) {
		errors.push("La password deve contenere almeno 8 caratteri");
	}

	if (!/[A-Z]/.test(password)) {
		errors.push("La password deve contenere almeno una lettera maiuscola");
	}

	if (!/[0-9]/.test(password)) {
		errors.push("La password deve contenere almeno un numero");
	}

	if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
		errors.push(
			"La password deve contenere almeno un carattere speciale (!@#$%^&* ecc.)",
		);
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}

export const useAuthStore = create<AuthState>((set, get) => ({
	user: null,
	profile: null,
	loading: true,
	isAdmin: false,
	adminVerificationCache: null,
	adminVerificationCacheTime: 0,
	adminVerificationCacheToken: null,
	setUser: (user) => set({ user, loading: false }),
	setProfile: (profile) => set({ profile }),
	checkUser: async () => {
		try {
			// Get the current session instead of just the user
			const { data: sessionData, error: sessionError } =
				await supabase.auth.getSession();

			if (sessionError) {
				console.error("Error getting session:", sessionError);
				set({ user: null, profile: null, loading: false });
				return;
			}

			// If we have a session, get the user
			if (sessionData?.session) {
				const { data: userData } = await supabase.auth.getUser();
				set({ user: userData.user, loading: false });

				// If user exists, fetch their profile
				if (userData.user) {
					await get().fetchUserProfile(userData.user.id);
				}
			} else {
				set({ user: null, profile: null, loading: false });
			}
		} catch (error) {
			console.error("Error checking user:", error);
			set({ user: null, profile: null, loading: false });
		}
	},
	fetchUserProfile: async (userId) => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", userId)
				.single();

			if (error) {
				console.error("Error fetching user profile:", error);

				// If the profile doesn't exist, create one
				if (error.code === "PGRST116") {
					const { data: userData } = await supabase.auth.getUser();
					if (userData.user) {
						const username = userData.user.email?.split("@")[0] || "user";
						const { data: newProfile, error: createError } = await supabase
							.from("profiles")
							.insert({
								id: userId,
								username: username,
								created_at: new Date().toISOString(),
							})
							.select()
							.single();

						if (createError) {
							console.error("Error creating user profile:", createError);
						} else if (newProfile) {
							set({ profile: newProfile as UserProfile });
						}
					}
				}
				return;
			}

			if (data) {
				set({ profile: data as UserProfile });
			}
		} catch (error) {
			console.error("Error fetching profile:", error);
		}
	},
	signIn: async (email, password) => {
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			if (!error && data?.user) {
				set({ user: data.user, loading: false });
				// Fetch user profile after successful sign in
				await get().fetchUserProfile(data.user.id);
			}

			return { error };
		} catch (error) {
			console.error("Sign in error:", error);
			return { error };
		}
	},
	signUp: async (email, password, username) => {
		try {
			// Validazione password
			const passwordValidation = validatePassword(password);
			if (!passwordValidation.isValid) {
				return {
					error: {
						message: passwordValidation.errors.join(". "),
					},
					data: null,
				};
			}

			// First check if username is already taken
			// profiles non è leggibile da anonimi: la RPC restituisce solo se
			// lo username esiste già.
			const { data: usernameTaken, error: checkError } = await supabase.rpc(
				"is_username_taken",
				{ p_username: username },
			);

			if (checkError) {
				console.error("Error checking username:", checkError);
				return { error: checkError, data: null };
			}

			if (usernameTaken) {
				return {
					error: {
						message: "Questo username è già in uso. Scegline un altro.",
					},
					data: null,
				};
			}

			// If username is available, proceed with signup
			const { data, error } = await supabase.auth.signUp({
				email,
				password,
				options: {
					data: {
						username: username,
					},
				},
			});

			if (!error && data?.user) {
				// Create the profile manually to ensure it exists
				const { error: profileError } = await supabase.from("profiles").insert({
					id: data.user.id,
					username: username,
					created_at: new Date().toISOString(),
				});

				if (profileError) {
					console.error("Error creating profile:", profileError);
				} else {
					// Fetch the newly created profile
					await get().fetchUserProfile(data.user.id);
				}

				set({
					user: data.user,
					loading: false,
				});
			}

			return { error, data };
		} catch (error) {
			console.error("Sign up error:", error);
			return { error, data: null };
		}
	},
	signOut: async () => {
		try {
			await supabase.auth.signOut();
			set({
				user: null,
				profile: null,
				isAdmin: false,
				adminVerificationCache: null,
				adminVerificationCacheTime: 0,
				adminVerificationCacheToken: null,
			});
		} catch (error) {
			console.error("Sign out error:", error);
		}
	},
	verifyAdminRole: async (forceRefresh = false): Promise<AdminVerification> => {
		const now = Date.now();
		const state = get();

		try {
			// Get current session to get auth token
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			if (sessionError || !session) {
				console.warn("No active session for admin verification");
				set({ isAdmin: false, adminVerificationCache: null });
				return { isAdmin: false };
			}

			// Cache valida solo per lo stesso token: la verifica fatta prima
			// della 2FA (isAdmin false, mfaRequired true) non deve sopravvivere
			// al token aal2 ottenuto con il codice.
			if (
				!forceRefresh &&
				state.adminVerificationCache &&
				state.adminVerificationCacheToken === session.access_token &&
				now - state.adminVerificationCacheTime < ADMIN_CACHE_TTL
			) {
				return state.adminVerificationCache;
			}

			// Call the verify-admin edge function with the auth token
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-admin`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
						apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
					},
				},
			);

			if (!response.ok) {
				console.warn("Admin verification failed:", response.statusText);
				set({ isAdmin: false, adminVerificationCache: null });
				return { isAdmin: false };
			}

			const data: AdminVerification = await response.json();

			// Update the store with admin status e cache
			set({
				isAdmin: data.isAdmin,
				adminVerificationCache: data,
				adminVerificationCacheTime: now,
				adminVerificationCacheToken: session.access_token,
			});

			return data;
		} catch (error) {
			console.error("Error verifying admin role:", error);
			set({ isAdmin: false, adminVerificationCache: null });
			return { isAdmin: false };
		}
	},
}));
